import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollToTop } from '../components/ScrollToTop';
import { PortalSelector, getAccessiblePortals } from '../components/PortalSelector';
import { 
  CustomerService, 
  EquipmentService, 
  SubscriptionService, 
  BillingService 
} from '../services/mockServices';
import { WEB_MANKLAR_URL } from '../lib/contacto';
import { 
  Home, Droplets, CreditCard, FileText, Wrench, 
  Users, HelpCircle, User, LogOut, Shield, Menu, X,
  ChevronRight, Calendar, Clock, CheckCircle, AlertCircle,
  Smartphone, Settings, Gift, Sparkles, ExternalLink,
  Leaf, ShieldCheck, Award, TrendingUp, Star
} from 'lucide-react';

// ============================================================
// ANIMATED CONTAINER - Fade in on mount
// ============================================================
function AnimatedContainer({ children, delay = 0, className = '' }) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div className={`transition-all duration-700 ease-out ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
    } ${className}`}>
      {children}
    </div>
  );
}

// ============================================================
// IMPACT CARD - For environmental metrics
// ============================================================
function ImpactCard({ icon: Icon, value, label, suffix, color, delay = 0 }) {
  const colorStyles = {
    water: 'from-sky-500/20 to-sky-500/5 border-sky-500/20 text-sky-400',
    eco: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/20 text-emerald-400',
    plastic: 'from-teal-500/20 to-teal-500/5 border-teal-500/20 text-teal-400'
  };

  return (
    <AnimatedContainer delay={delay}>
      <div className={`relative p-5 rounded-2xl bg-gradient-to-br ${colorStyles[color]} border overflow-hidden group hover:scale-[1.02] transition-transform duration-300`}>
        <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full blur-2xl -mr-10 -mt-10" />
        <Icon className={`w-6 h-6 mb-3 ${colorStyles[color].split(' ').pop()}`} />
        <div className="text-3xl sm:text-4xl font-light text-white font-['Outfit'] mb-1">
          {value.toLocaleString()}<span className="text-lg ml-1">{suffix}</span>
        </div>
        <p className="text-xs text-zinc-400 font-['Manrope']">{label}</p>
      </div>
    </AnimatedContainer>
  );
}

// ============================================================
// PREMIUM DASHBOARD CARD
// ============================================================
function PremiumCard({ 
  icon: Icon, 
  title, 
  subtitle, 
  children, 
  to, 
  onClick,
  variant = 'default',
  badge,
  iconColor = 'default',
  className = '' 
}) {
  const baseStyles = `
    relative p-5 sm:p-6 rounded-2xl border transition-all duration-500 
    hover:-translate-y-1 hover:shadow-xl group overflow-hidden
  `;
  
  const variants = {
    default: 'bg-white/[0.02] border-white/[0.08] hover:border-white/20 hover:bg-white/[0.04]',
    highlight: 'bg-gradient-to-br from-white/[0.05] to-white/[0.02] border-white/[0.12] hover:border-white/25',
    success: 'bg-emerald-500/[0.05] border-emerald-500/20 hover:border-emerald-500/40',
    warning: 'bg-amber-500/[0.03] border-amber-500/15 hover:border-amber-500/30',
    accent: 'bg-gradient-to-br from-sky-500/[0.08] to-purple-500/[0.05] border-sky-500/20 hover:border-sky-500/40',
    referral: 'bg-gradient-to-br from-amber-500/[0.08] to-orange-500/[0.05] border-amber-500/20 hover:border-amber-500/40'
  };

  const iconColors = {
    default: 'text-zinc-400 group-hover:text-white bg-white/[0.08] group-hover:bg-white/[0.12]',
    water: 'text-sky-400 bg-sky-500/20 group-hover:bg-sky-500/30',
    success: 'text-emerald-400 bg-emerald-500/20 group-hover:bg-emerald-500/30',
    warning: 'text-amber-400 bg-amber-500/20 group-hover:bg-amber-500/30',
    gold: 'text-amber-400 bg-amber-500/20 group-hover:bg-amber-500/30',
    accent: 'text-sky-400 bg-sky-500/20 group-hover:bg-sky-500/30',
    tech: 'text-blue-400 bg-blue-500/20 group-hover:bg-blue-500/30'
  };

  const content = (
    <div className={`${baseStyles} ${variants[variant]} ${className}`}>
      {/* Subtle glow on hover */}
      <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
        variant === 'success' ? 'bg-emerald-500/10' :
        variant === 'warning' ? 'bg-amber-500/10' :
        variant === 'accent' ? 'bg-sky-500/10' :
        variant === 'referral' ? 'bg-amber-500/10' :
        'bg-white/5'
      }`} />
      
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${iconColors[iconColor]}`}>
              <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-medium text-white font-['Outfit']">{title}</h3>
              {subtitle && <p className="text-xs text-zinc-500 font-['Manrope']">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {badge && (
              <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                {badge}
              </span>
            )}
            {(to || onClick) && (
              <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 group-hover:translate-x-0.5 transition-all" />
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  if (to) return <Link to={to} className="block">{content}</Link>;
  if (onClick) return <button onClick={onClick} className="block w-full text-left">{content}</button>;
  return content;
}

// ============================================================
// STATUS BADGE WITH ANIMATED DOT
// ============================================================
function StatusBadge({ status }) {
  const statusConfig = {
    active: { label: 'Activo', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', dot: 'bg-emerald-400' },
    pending: { label: 'Pendiente', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', dot: 'bg-amber-400' },
    paid: { label: 'Pagado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', dot: 'bg-emerald-400' },
    overdue: { label: 'Vencido', color: 'bg-red-500/20 text-red-400 border-red-500/30', dot: 'bg-red-400' },
    scheduled: { label: 'Programado', color: 'bg-sky-500/20 text-sky-400 border-sky-500/30', dot: 'bg-sky-400' },
    maintenance: { label: 'En revisión', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', dot: 'bg-orange-400' }
  };

  const config = statusConfig[status] || statusConfig.active;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot} animate-pulse`} />
      {config.label}
    </span>
  );
}

// ============================================================
// PROGRESS BAR FOR REFERRALS
// ============================================================
function ReferralProgress({ current, target, bonus }) {
  const percentage = Math.min((current / target) * 100, 100);
  const remaining = target - current;

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-500 font-['Manrope']">{current} / {target} referidos validados</span>
        <span className="text-xs text-amber-400 font-medium">{bonus} meses gratis</span>
      </div>
      <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {remaining > 0 && (
        <p className="text-xs text-zinc-600 mt-2 font-['Manrope']">
          Te faltan <span className="text-amber-400">{remaining} referidos</span> para conseguir +6 meses GRATIS
        </p>
      )}
    </div>
  );
}

// ============================================================
// MAINTENANCE COUNTDOWN - Never show negative days
// ============================================================
function MaintenanceCountdown({ nextDate }) {
  if (!nextDate) {
    return (
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-xs text-zinc-500 font-['Manrope']">Próximo mantenimiento</span>
        </div>
        <span className="text-sm font-medium text-amber-400 font-['Outfit']">
          Pendiente de programar
        </span>
      </div>
    );
  }

  const today = new Date();
  const maintenance = new Date(nextDate);
  const diffTime = maintenance - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Never show negative days
  if (diffDays < 0) {
    return (
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          <span className="text-xs text-zinc-500 font-['Manrope']">Próximo mantenimiento</span>
        </div>
        <span className="text-sm font-medium text-red-400 font-['Outfit']">
          Vencido - Solicitar revisión
        </span>
      </div>
    );
  }

  const getStatusColor = () => {
    if (diffDays <= 7) return 'text-amber-400';
    if (diffDays <= 30) return 'text-sky-400';
    return 'text-emerald-400';
  };

  const getStatusDot = () => {
    if (diffDays <= 7) return 'bg-amber-400';
    if (diffDays <= 30) return 'bg-sky-400';
    return 'bg-emerald-400';
  };

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${getStatusDot()} animate-pulse`} />
        <span className="text-xs text-zinc-500 font-['Manrope']">Próximo mantenimiento</span>
      </div>
      <span className={`text-sm font-medium ${getStatusColor()} font-['Outfit']`}>
        En {diffDays} días
      </span>
    </div>
  );
}

// ============================================================
// MOBILE NAVIGATION PREMIUM
// ============================================================
function MobileNav({ isOpen, onClose, isAdmin, onLogout, userName, userRole }) {
  if (!isOpen) return null;

  const navItems = [
    { to: '/dashboard', icon: Home, label: 'Inicio', active: true },
    { to: '/equipment', icon: Droplets, label: 'Mis Equipos', color: 'text-sky-400' },
    { to: '/subscription', icon: CreditCard, label: 'Suscripción' },
    { to: '/billing', icon: FileText, label: 'Facturación', color: 'text-amber-400' },
    { to: '/maintenance', icon: Wrench, label: 'Mantenimiento', color: 'text-blue-400' },
    { to: '/referrals', icon: Gift, label: 'Referidos', color: 'text-amber-400' },
    { to: '/help', icon: HelpCircle, label: 'Ayuda' },
    { to: '/profile', icon: User, label: 'Mi Perfil' },
  ];

  // Check if user has multiple portals
  const accessiblePortals = getAccessiblePortals(userRole);
  const hasMultiplePortals = accessiblePortals.length > 1;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-40" onClick={onClose} />
      <div className="fixed top-0 left-0 h-full w-72 bg-[#030303] border-r border-white/[0.08] z-50 overflow-y-auto">
        {/* Header with user info */}
        <div className="p-5 border-b border-white/[0.06]">
          <div className="flex items-center justify-between mb-4">
            <img src="/manklar-logo-white.png" alt="Manklar" className="h-8 drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]" />
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* User card */}
          <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <p className="text-sm font-medium text-white font-['Outfit']">{userName}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
              <span className="text-xs text-zinc-500 font-['Manrope']">Cliente Manklar</span>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {navItems.map((item) => (
            <Link 
              key={item.to}
              to={item.to} 
              onClick={onClose} 
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                item.active 
                  ? 'text-white bg-white/10' 
                  : 'text-zinc-400 hover:text-white hover:bg-white/[0.05]'
              }`}
            >
              <item.icon className={`w-5 h-5 ${item.color || ''}`} />
              <span className="font-['Manrope']">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Portal Selector (if user has access to multiple portals) */}
        {hasMultiplePortals && (
          <div className="px-3 py-4 border-t border-white/[0.06]">
            <PortalSelector variant="full" showDescription={false} />
          </div>
        )}

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06] bg-[#030303]">
          <button 
            onClick={() => { onLogout(); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-['Manrope']">Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================================
// MAIN DASHBOARD COMPONENT
// ============================================================
export default function MainDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [equipments, setEquipments] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const isAdmin = user?.role === 'admin';
  const userName = user?.name || profile?.name || 'Cliente';
  const firstName = userName.split(' ')[0];

  // Simulated impact data (would come from backend)
  const impactData = {
    litersFiltered: 4200,
    bottlesAvoided: 840,
    plasticSaved: 32
  };

  const fetchData = useCallback(async () => {
    try {
      const [profileData, equipmentData, subscriptionData, invoiceData] = await Promise.all([
        CustomerService.getProfile(user?.id),
        EquipmentService.getEquipments(user?.id),
        SubscriptionService.getSubscription(user?.id),
        BillingService.getInvoices(user?.id)
      ]);
      
      setProfile(profileData);
      setEquipments(equipmentData);
      setSubscription(subscriptionData);
      setInvoices(invoiceData);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const pendingInvoice = invoices.find(inv => inv.status === 'pending');
  const equipment = equipments[0];

  // Simulated referral data
  const referralData = { current: 1, target: 10, bonus: 6 };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src="/manklar-logo-white.png" alt="Manklar" className="h-12 opacity-50 animate-pulse" />
          <div className="w-10 h-10 border-2 border-white/10 border-t-white/50 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Premium Header */}
      <header className="sticky top-0 z-30 bg-[#030303]/90 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden w-10 h-10 rounded-xl bg-white/[0.05] flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.08] transition-all"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center">
              <img 
                src="/manklar-logo-white.png" 
                alt="Manklar" 
                className="h-9 sm:h-11 drop-shadow-[0_0_25px_rgba(255,255,255,0.12)]"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {[
              { to: '/equipment', label: 'Equipos', icon: Droplets, color: 'hover:text-sky-400' },
              { to: '/subscription', label: 'Suscripción', icon: CreditCard },
              { to: '/billing', label: 'Facturación', icon: FileText, color: 'hover:text-amber-400' },
              { to: '/maintenance', label: 'Mantenimiento', icon: Wrench, color: 'hover:text-blue-400' },
              { to: '/referrals', label: 'Referidos', icon: Gift, color: 'hover:text-amber-400' },
            ].map((item) => (
              <Link 
                key={item.to}
                to={item.to} 
                className={`px-4 py-2 text-sm text-zinc-400 hover:text-white rounded-lg hover:bg-white/[0.05] transition-all font-['Manrope'] ${item.color || ''}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/help" className="hidden sm:flex">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white hover:bg-white/[0.05] rounded-lg">
                <HelpCircle className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline font-['Manrope']">Ayuda</span>
              </Button>
            </Link>
            <Link to="/profile" className="hidden sm:flex">
              <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white hover:bg-white/[0.05] rounded-lg">
                <User className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline font-['Manrope']">Perfil</span>
              </Button>
            </Link>
            {isAdmin && (
              <Link to="/admin" className="hidden sm:flex">
                <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white hover:bg-white/[0.05] rounded-lg">
                  <Shield className="w-4 h-4" />
                </Button>
              </Link>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white hover:bg-white/[0.05] rounded-lg"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <MobileNav 
        isOpen={mobileNavOpen} 
        onClose={() => setMobileNavOpen(false)}
        isAdmin={isAdmin}
        onLogout={handleLogout}
        userName={userName}
        userRole={user?.role}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Welcome Hero - More Emotional */}
        <AnimatedContainer delay={0}>
          <div className="mb-8 sm:mb-10 relative">
            <div className="absolute top-0 left-0 w-72 h-72 bg-sky-500/5 rounded-full blur-[120px] -z-10" />
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white font-['Outfit'] mb-3">
              Hola, {firstName} 👋
            </h1>
            <p className="text-base sm:text-lg text-zinc-500 font-['Manrope'] max-w-lg">
              Tu agua, tu servicio y tus beneficios en un solo lugar.
            </p>
          </div>
        </AnimatedContainer>

        {/* Manklar Impact Panel - WOW Factor */}
        <AnimatedContainer delay={100}>
          <div className="mb-8 sm:mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-sky-400" />
              <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-['Manrope']">Tu impacto Manklar</h2>
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              <ImpactCard 
                icon={Droplets} 
                value={impactData.litersFiltered} 
                suffix="L"
                label="Litros filtrados" 
                color="water"
                delay={150}
              />
              <ImpactCard 
                icon={Leaf} 
                value={impactData.bottlesAvoided} 
                label="Botellas evitadas" 
                color="eco"
                delay={200}
              />
              <ImpactCard 
                icon={ShieldCheck} 
                value={impactData.plasticSaved} 
                suffix="kg"
                label="Plástico ahorrado" 
                color="plastic"
                delay={250}
              />
            </div>
          </div>
        </AnimatedContainer>

        {/* Payment Alert - More Elegant */}
        {pendingInvoice && (
          <AnimatedContainer delay={300}>
            <div className="mb-8 p-4 rounded-xl bg-amber-500/[0.05] border border-amber-500/15 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-300 font-['Outfit']">Factura pendiente</p>
                <p className="text-xs text-zinc-500 font-['Manrope'] truncate">
                  {pendingInvoice.number} • {pendingInvoice.amount.toFixed(2)}€
                </p>
              </div>
              <Link to="/billing">
                <Button size="sm" className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 rounded-lg px-4 text-xs font-semibold">
                  Pagar
                </Button>
              </Link>
            </div>
          </AnimatedContainer>
        )}

        {/* Premium Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {/* Equipment Card - Premium Product Feel */}
          <AnimatedContainer delay={350}>
            <PremiumCard
              icon={Droplets}
              title="Mi Equipo"
              subtitle={equipment?.name || 'Sin equipo'}
              to="/equipment"
              variant={equipment?.status === 'maintenance' ? 'warning' : 'default'}
              iconColor="water"
            >
              {equipment ? (
                <div className="space-y-1 mt-3">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs text-zinc-500 font-['Manrope']">Estado</span>
                    <StatusBadge status={equipment.status} />
                  </div>
                  <MaintenanceCountdown nextDate={equipment.nextMaintenance} />
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs text-zinc-500 font-['Manrope']">Garantía</span>
                    <span className="text-xs text-emerald-400 font-medium">Activa</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-zinc-500 font-['Manrope'] mt-3">No tienes equipos instalados</p>
              )}
            </PremiumCard>
          </AnimatedContainer>

          {/* Subscription Card */}
          <AnimatedContainer delay={400}>
            <PremiumCard
              icon={CreditCard}
              title="Mi Suscripción"
              subtitle={subscription?.planName}
              to="/subscription"
              variant="highlight"
              iconColor="success"
            >
              <div className="space-y-1 mt-3">
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs text-zinc-500 font-['Manrope']">Estado</span>
                  <StatusBadge status={subscription?.status || 'active'} />
                </div>
                <div className="flex items-center justify-between py-3 mt-2 border-t border-white/[0.06]">
                  <span className="text-xs text-zinc-500 font-['Manrope']">Cuota mensual</span>
                  <div className="text-right">
                    <span className="text-2xl font-light text-white font-['Outfit']">
                      {subscription?.monthlyPrice?.toFixed(2)}€
                    </span>
                    <span className="text-xs text-zinc-600 block">IVA incl.</span>
                  </div>
                </div>
              </div>
            </PremiumCard>
          </AnimatedContainer>

          {/* Billing Card */}
          <AnimatedContainer delay={450}>
            <PremiumCard
              icon={FileText}
              title="Facturación"
              subtitle={pendingInvoice ? 'Pago pendiente' : 'Al corriente'}
              to="/billing"
              variant={pendingInvoice ? 'warning' : 'success'}
              iconColor="gold"
              badge={pendingInvoice ? '1' : null}
            >
              <div className="space-y-1 mt-3">
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs text-zinc-500 font-['Manrope']">Próxima factura</span>
                  <span className="text-sm font-medium text-white">{subscription?.monthlyPrice?.toFixed(2)}€</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs text-zinc-500 font-['Manrope']">Fecha</span>
                  <span className="text-sm text-zinc-400">{new Date(subscription?.nextBillingDate).toLocaleDateString('es-ES')}</span>
                </div>
              </div>
            </PremiumCard>
          </AnimatedContainer>

          {/* Maintenance Card */}
          <AnimatedContainer delay={500}>
            <PremiumCard
              icon={Wrench}
              title="Mantenimiento"
              subtitle="Asistencia técnica"
              to="/maintenance"
              iconColor="tech"
            >
              <p className="text-xs text-zinc-500 font-['Manrope'] mt-3 mb-4">
                Programa una revisión o reporta cualquier incidencia.
              </p>
              <Button 
                size="sm" 
                variant="outline" 
                className="w-full border-white/[0.12] text-white hover:bg-white/[0.08] rounded-xl font-['Manrope'] text-xs"
              >
                Nueva solicitud
              </Button>
            </PremiumCard>
          </AnimatedContainer>

          {/* Referrals Card with Gamification */}
          <AnimatedContainer delay={550}>
            <PremiumCard
              icon={Gift}
              title="Programa de Referidos"
              subtitle="Invita amigos y gana"
              to="/referrals"
              variant="referral"
              iconColor="gold"
            >
              <p className="text-xs text-zinc-400 font-['Manrope'] mt-3">
                Por cada referido validado recibes <span className="text-amber-400 font-medium">1 mes gratis</span>
              </p>
              <ReferralProgress 
                current={referralData.current} 
                target={referralData.target} 
                bonus={referralData.bonus}
              />
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] mt-4">
                <span className="text-xs text-zinc-500 font-['Manrope']">Tu código:</span>
                <span className="font-mono text-sm text-white bg-amber-500/20 px-3 py-1 rounded-lg border border-amber-500/30">
                  {user?.referral_code || 'DEMO123'}
                </span>
              </div>
            </PremiumCard>
          </AnimatedContainer>

          {/* Help Card */}
          <AnimatedContainer delay={600}>
            <PremiumCard
              icon={HelpCircle}
              title="Centro de Ayuda"
              subtitle="Soporte 24/7"
              to="/help"
            >
              <p className="text-xs text-zinc-500 font-['Manrope'] mt-3 mb-4">
                Encuentra respuestas o contacta con nuestro equipo.
              </p>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open('https://wa.me/34623243967', '_blank', 'noopener,noreferrer');
                }}
                className="flex items-center justify-center gap-2 w-full h-10 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-[#25D366]/20 font-['Manrope'] text-xs"
              >
                <Smartphone className="w-4 h-4" />
                WhatsApp Soporte
              </button>
            </PremiumCard>
          </AnimatedContainer>
        </div>

        {/* Quick Actions */}
        <AnimatedContainer delay={650}>
          <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-white/[0.015] border border-white/[0.05] relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-sky-500/5 to-transparent rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="text-xs uppercase tracking-widest text-zinc-600 mb-5 font-['Manrope']">Acciones Rápidas</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { to: '/maintenance', icon: Wrench, label: 'Solicitar revisión', color: 'text-blue-400' },
                  { to: '/billing', icon: FileText, label: 'Ver facturas', color: 'text-amber-400' },
                  { to: '/referrals', icon: Users, label: 'Invita y gana', accent: true },
                  { to: '/profile', icon: Settings, label: 'Mi cuenta' },
                ].map((action, i) => (
                  <Link 
                    key={action.to}
                    to={action.to} 
                    className={`flex flex-col items-center gap-3 p-5 rounded-xl transition-all duration-300 hover:-translate-y-1 ${
                      action.accent 
                        ? 'bg-emerald-500/[0.08] hover:bg-emerald-500/[0.12] border border-emerald-500/20' 
                        : 'bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.05]'
                    }`}
                  >
                    <action.icon className={`w-6 h-6 ${action.accent ? 'text-emerald-400' : action.color || 'text-zinc-400'}`} />
                    <span className={`text-xs text-center font-['Manrope'] ${action.accent ? 'text-emerald-300' : 'text-zinc-400'}`}>
                      {action.label}
                    </span>
                  </Link>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/[0.05]">
                <a
                  href={WEB_MANKLAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-sm text-zinc-600 hover:text-white transition-colors font-['Manrope']"
                >
                  <ExternalLink className="w-4 h-4" />
                  Visitar Web Manklar
                </a>
              </div>
            </div>
          </div>
        </AnimatedContainer>
      </main>

      <ScrollToTop />
    </div>
  );
}
